import { promiseState } from '@passionware/platform-react';
import { useCallback } from 'react';
import { Button } from 'v5/design-system/Button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from 'v5/design-system/Dialog';
import { MountElementProps } from 'v5/messages/mount-element';
import { AsyncButton } from 'v5/patterns/forms/fields/AsyncButton';

export interface DeleteConfirmationDialogProps extends MountElementProps {
  /**
   * Optional custom title for the dialog
   */
  title?: string;
  /**
   * Optional custom description for the dialog
   */
  description?: string;
  /**
   * Callback when user confirms (returns true) or cancels (returns false)
   */
  onResolve: (confirmed: boolean) => Promise<void>;
}

/**
 * Delete Confirmation Dialog - Pure dialog component
 *
 * Designed to be used with MountElementHandler via user flow service.
 * This ensures animations complete even if the trigger unmounts.
 *
 * Usage via CockpitUserFlowService:
 * ```
 * if (await myServices.userFlowService.confirmDelete({ description: '...' })) {
 *   await deleteMutation.track(id);
 * }
 * ```
 */
export function DeleteConfirmationDialog({
  open,
  onOpenChange,
  close,
  title = 'Delete',
  description = 'Are you sure you want to delete this item? This action cannot be undone.',
  onResolve
}: DeleteConfirmationDialogProps) {
  const confirmMutation = promiseState.useMutation(async () => {
    await onResolve(true);
    close();
  });

  const handleCancel = useCallback(async () => {
    // Resolve with false (cancelled) and close dialog
    await onResolve(false);
    close();
  }, [close, onResolve]);

  const handleOpenChange = useCallback(
    async (newOpen: boolean) => {
      onOpenChange(newOpen);
      // If dialog closes without explicit confirmation/cancel (e.g., ESC key, backdrop click)
      // resolve as cancelled, but only if we haven't already resolved
      if (!newOpen) {
        await onResolve(false);
        close();
      }
    },
    [onOpenChange, onResolve, close]
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogDescription className="pt-6 pb-6 text-base">
          {description}
        </DialogDescription>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
          </DialogClose>
          <AsyncButton
            onClick={() => confirmMutation.track(undefined)}
            mutation={confirmMutation.state}
            variant="danger"
          >
            Delete
          </AsyncButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
